import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
var SCROLL_THROTTLE_TIMEOUT = 400;
export function usePopoverOnInteraction(_a) {
    var listenOnClicks = _a.listenOnClicks, listenOnHover = _a.listenOnHover, _b = _a.hideOnScroll, hideOnScroll = _b === void 0 ? true : _b, _c = _a.hoverDelay, hoverDelay = _c === void 0 ? 1 : _c, onVisible = _a.onVisible, onHide = _a.onHide;
    var _d = useState(false), isVisible = _d[0], setIsVisible = _d[1];
    var isMounted = useIsMounted();
    var hoverTimerId = useRef();
    var hideTimerId = useRef();
    var wasOpenedByClick = useRef(false);
    var showPopoverExtras = useCallback(function () {
        if (!isMounted) {
            return;
        }
        onVisible && onVisible();
    }, [isMounted, onVisible]);
    var showPopover = useCallback(function () {
        setIsVisible(true);
        showPopoverExtras();
    }, [showPopoverExtras]);
    var hidePopoverExtras = useCallback(function () {
        if (!isMounted) {
            return;
        }
        hoverTimerId.current && clearTimeout(hoverTimerId.current);
        wasOpenedByClick.current = false;
        onHide && onHide();
    }, [isMounted, onHide]);
    var hidePopover = useCallback(function () {
        hidePopoverExtras();
        setIsVisible(false);
    }, [hidePopoverExtras]);
    var hidePopoverDebounced = useMemo(function () {
        return throttle(hidePopover, SCROLL_THROTTLE_TIMEOUT, {
            leading: true,
        });
    }, [hidePopover]);
    var togglePopover = useCallback(function () {
        return setIsVisible(function (prevValue) {
            var willBeVisible = !prevValue;
            willBeVisible ? showPopoverExtras() : hidePopoverExtras();
            return willBeVisible;
        });
    }, [showPopoverExtras, hidePopoverExtras]);
    useEffect(function () {
        if (!hideOnScroll || !isVisible) {
            return;
        }
        window.addEventListener('scroll', hidePopoverDebounced);
        return function () { return window.removeEventListener('scroll', hidePopoverDebounced); };
    }, [hideOnScroll, hidePopoverDebounced, isVisible]);
    var onClick = useCallback(function () {
        if (!listenOnClicks) {
            return;
        }
        wasOpenedByClick.current = true;
        togglePopover();
    }, [listenOnClicks, togglePopover]);
    var onMouseEnter = useCallback(function () {
        if (!listenOnHover || wasOpenedByClick.current) {
            return;
        }
        hideTimerId.current && clearTimeout(hideTimerId.current);
        hoverTimerId.current = window.setTimeout(function () { return showPopover(); }, hoverDelay);
    }, [listenOnHover, hoverDelay, showPopover]);
    var onMouseLeave = useCallback(function () {
        if (!listenOnHover || wasOpenedByClick.current) {
            return;
        }
        // a small delay on hiding is needed to handle both tooltip and reference hovers
        hideTimerId.current = window.setTimeout(function () { return hidePopover(); }, 100);
    }, [listenOnHover, hidePopover]);
    var onClose = useCallback(function () { return hidePopover(); }, [hidePopover]);
    useEffect(function () {
        return function () {
            hideTimerId.current && clearTimeout(hideTimerId.current);
            hoverTimerId.current && clearTimeout(hoverTimerId.current);
            hidePopoverDebounced.cancel();
            window.removeEventListener('wheel', hidePopoverDebounced);
        };
    }, [hidePopoverDebounced]);
    return { onClick: onClick, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onClose: onClose, isVisible: isVisible };
}
//# sourceMappingURL=usePopoverOnInteraction.js.map